import {Component, Input, OnInit} from "@angular/core";
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from "@angular/forms";
import {take} from "rxjs/operators";
import {FieldSuggestionOption} from "../../../shared/models/fieldSuggestion";
import {FieldSuggestionService} from "../../../shared/services/field-suggestion/field-suggestion.service";

// Reusable multi-choice checklist fed by an admin-curated suggestion list
// (FieldSuggestion). Selected values are stored as a single string joined with
// "|". An option flagged is_free_text (« Autre ») reveals a free text input
// whose content replaces the option label in the output. Reused on signup and
// the attendance-confirmation modal (and meant for card 4).
@Component({
    "selector": "app-suggestion-checklist",
    "templateUrl": "./suggestion-checklist.component.html",
    "providers": [
        {
            "provide": NG_VALUE_ACCESSOR,
            "multi": true,
            "useExisting": SuggestionChecklistComponent
        }
    ]
})
export class SuggestionChecklistComponent implements ControlValueAccessor, OnInit {
    @Input() category: string;

    options: Array<FieldSuggestionOption> = [];

    checkedValues: {[value: string]: boolean} = {};

    freeText = "";

    disabled = false;

    touched = false;

    // Value received before the options are loaded, reconciled in ngOnInit
    private pendingValue: string = null;

    onChange = (value) => {};

    onTouched = () => {};

    constructor (private fieldSuggestionService: FieldSuggestionService) {}

    ngOnInit () {
        this.fieldSuggestionService.getOptions(this.category).
            pipe(take(1)).
            subscribe((options) => {
                this.options = options;
                this.reconcile();
            });
    }

    // Split the "|"-joined model value: known fixed options are checked; any
    // token matching no fixed option is treated as the free-text value.
    private reconcile () {
        if (!this.pendingValue) {
            return;
        }

        const tokens = this.pendingValue.split("|").
            map((token) => token.trim()).
            filter((token) => token.length > 0);

        const freeOption = this.options.find((option) => option.is_free_text);

        for (const token of tokens) {
            const fixedMatch = this.options.find(
                (option) => !option.is_free_text && option.value === token
            );
            if (fixedMatch) {
                this.checkedValues[fixedMatch.value] = true;
            } else if (freeOption) {
                this.checkedValues[freeOption.value] = true;
                this.freeText = token;
            }
        }

        this.pendingValue = null;
    }

    onToggle (option: FieldSuggestionOption, checked: boolean) {
        this.markAsTouched();
        if (this.disabled) {
            return;
        }
        this.checkedValues[option.value] = checked;
        if (option.is_free_text && !checked) {
            this.freeText = "";
        }
        this.emit();
    }

    onFreeTextChange (text: string) {
        this.markAsTouched();
        if (this.disabled) {
            return;
        }
        this.freeText = text;
        this.emit();
    }

    private emit () {
        const parts: Array<string> = [];

        for (const option of this.options) {
            if (!this.checkedValues[option.value]) {
                continue;
            }
            if (option.is_free_text) {
                if (this.freeText) {
                    parts.push(this.freeText);
                }
            } else {
                parts.push(option.value);
            }
        }

        this.onChange(parts.join("|"));
    }

    writeValue (value: string) {
        this.pendingValue = value;
        this.checkedValues = {};
        this.freeText = "";
        if (this.options.length) {
            this.reconcile();
        }
    }

    registerOnChange (onChange: any) {
        this.onChange = onChange;
    }

    registerOnTouched (onTouched: any) {
        this.onTouched = onTouched;
    }

    markAsTouched () {
        if (!this.touched) {
            this.onTouched();
            this.touched = true;
        }
    }

    setDisabledState (disabled: boolean) {
        this.disabled = disabled;
    }
}
